// Criterion form fields shared by CriteriaList's add/edit modal and CreatePlanWizard's per-phase
// criterion form (BlockForm.tsx precedent for extracted shared form pieces).
// Mirrors docs/superpowers/specs/2026-08-29-rtp-planner-api-reference.md §7.
import { useState } from "react";
import { Button, Form, Input, InputNumber, Segmented, Select, Tooltip } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { RtpCriterionDetails, RtpMetricStatRow } from "./types";
import { coachName, useRtpMetricOptions, useRtpMetricStats } from "./rtpHooks";
import type { Coach } from "./rtpHooks";

export const COMPARISON_OPTIONS = [
	{ value: ">=", label: ">=" },
	{ value: "<=", label: "<=" },
];

export interface CriterionFormValues {
	criterionType: "event" | "metric";
	description: string;
	metricSource?: "gps" | "testing" | "forceplate" | "nutrition" | "wellness";
	metricKey?: string;
	comparison?: ">=" | "<=";
	threshold?: number;
	allowedSigners: string[];
}

// Builds `details` with only the current type's keys, in a fixed key order — run on both the
// form values and the stored criterion so the two are directly comparable (see CriteriaList's
// detailsChanged).
export function buildDetails(input: RtpCriterionDetails, type: "event" | "metric"): RtpCriterionDetails {
	if (type === "event") return { description: input.description };
	return {
		description: input.description,
		metricSource: input.metricSource,
		metricKey: input.metricKey,
		comparison: input.comparison,
		threshold: input.threshold,
	};
}

// No thousands separators on purpose — values must read like what lands in the threshold field.
export const formatStat = (value: number | string): string =>
	typeof value === "number" ? String(Math.round(value * 10) / 10) : value;

// The 85% recommendation anchors on the best available stat, in this priority order.
const RECOMMENDATION_PRIORITY = ["max", "baseline", "histAvg"];

interface MetricStatsHintProps {
	player: { id: number; name: string };
	metricKey?: string;
	applyTooltip: string;
	// data-attr prefix for the apply buttons, e.g. "settings.rtp-criterion" -> ".rec-apply"/".calc-apply".
	dataAttrPrefix: string;
	onApply: (value: number) => void;
}

// The player-stats hint panel: stat rows, the 85% recommendation, and the DIY calculator.
// Shared by the exit criteria modal (fills Threshold) and field-mode day plans (fills Target).
export const MetricStatsHint = ({ player, metricKey, applyTooltip, dataAttrPrefix, onApply }: MetricStatsHintProps) => {
	const { byKey } = useRtpMetricOptions();
	const { rows: statRows } = useRtpMetricStats(player.id, metricKey);
	let recommendationFound: { label: string; value: number } | null = null;
	for (const key of RECOMMENDATION_PRIORITY) {
		const row = statRows.find((r) => r.key === key);
		if (row && typeof row.value === "number") {
			recommendationFound = { label: row.label, value: Math.round(row.value * 0.85 * 10) / 10 };
			break;
		}
	}
	// const so the click handler's closure keeps the non-null narrowing from the JSX guard.
	const recommendation = recommendationFound;

	// DIY calculator: <stat> × <pct>% = <result>, click the result to apply it. calcLabel falls
	// back to the recommendation's anchor stat when unset or gone after a metric switch, so no
	// reset effect is needed.
	const [calcLabel, setCalcLabel] = useState<string | null>(null);
	const [calcPct, setCalcPct] = useState<number | null>(85);
	// The user can close the panel. Each metric pick opens it again, also a return to a metric
	// the user closed before. The reset happens during render, so no effect is needed.
	const [dismissed, setDismissed] = useState(false);
	const [pickedKey, setPickedKey] = useState(metricKey);
	if (pickedKey !== metricKey) {
		setPickedKey(metricKey);
		setDismissed(false);
	}
	const numericRows = statRows.filter((r): r is RtpMetricStatRow & { value: number } => typeof r.value === "number");
	const calcRow =
		numericRows.find((r) => r.label === calcLabel) ??
		numericRows.find((r) => r.label === recommendation?.label) ??
		numericRows[0];
	const calcResult = calcRow && calcPct != null ? Math.round(((calcRow.value * calcPct) / 100) * 10) / 10 : null;

	if (!metricKey || !recommendation || dismissed) return null;

	return (
		<div className="rtp-metric-stats">
			<div className="rtp-metric-stats-head">
				<span>
					Here are some of {player.name}&rsquo;s {byKey[metricKey]?.label ?? metricKey} values.
				</span>
				<Button
					type="text"
					size="small"
					icon={<CloseOutlined />}
					aria-label="Hide these values"
					className="rtp-metric-stats-close"
					data-attr={`${dataAttrPrefix}.hint-dismiss`}
					onClick={() => setDismissed(true)}
				/>
			</div>
			{statRows.map((row) => (
				<div key={row.label}>
					{row.label}: <b>{formatStat(row.value)}</b>
				</div>
			))}
			<div className="rtp-metric-stats-rec">
				Based on this data, Whistle recommends 85% of {recommendation.label}:{" "}
				<Tooltip title={applyTooltip}>
					<Button
						type="link"
						size="small"
						className="rtp-metric-calc-result"
						data-attr={`${dataAttrPrefix}.rec-apply`}
						onClick={() => onApply(recommendation.value)}
					>
						{formatStat(recommendation.value)}
					</Button>
				</Tooltip>
			</div>
			{calcRow ? (
				<div className="rtp-metric-calc">
					<Select
						size="small"
						aria-label="Calculator stat"
						options={numericRows.map((r) => ({ value: r.label, label: r.label }))}
						value={calcRow.label}
						onChange={setCalcLabel}
					/>
					<span>×</span>
					<InputNumber
						size="small"
						aria-label="Percentage"
						min={0}
						max={999}
						suffix="%"
						value={calcPct}
						onChange={setCalcPct}
					/>
					<span>=</span>
					{calcResult != null ? (
						<Tooltip title={applyTooltip}>
							<Button
								type="link"
								size="small"
								className="rtp-metric-calc-result"
								data-attr={`${dataAttrPrefix}.calc-apply`}
								onClick={() => onApply(calcResult)}
							>
								{formatStat(calcResult)}
							</Button>
						</Tooltip>
					) : (
						<span>—</span>
					)}
				</div>
			) : null}
		</div>
	);
};

interface CriterionFormFieldsProps {
	criterionType: "event" | "metric" | undefined;
	disableType?: boolean;
	coachList: Coach[];
	extraSignerOptions?: { value: string; label: string }[];
	// When set, picking a metric fetches this player's historical values as a threshold hint.
	player?: { id: number; name: string };
}

export const CriterionFormFields = ({
	criterionType,
	disableType,
	coachList,
	extraSignerOptions = [],
	player,
}: CriterionFormFieldsProps) => {
	const form = Form.useFormInstance();
	const { groups, byKey } = useRtpMetricOptions();
	const metricKey = Form.useWatch("metricKey", form);
	// A stored key that predates the dropdown (free-text era) still needs to render and stay
	// selectable when editing.
	const metricOptions =
		metricKey && !byKey[metricKey]
			? [...groups, { label: "Other", title: "Other", options: [{ value: metricKey, label: metricKey }] }]
			: groups;

	return (
		<>
			<Form.Item name="criterionType" label="Type" rules={[{ required: true }]}>
				<Segmented
					block
					disabled={disableType}
					options={[
						{ label: "Event", value: "event" },
						{ label: "Metric", value: "metric" },
					]}
				/>
			</Form.Item>
			<Form.Item
				name="description"
				label="Description"
				rules={[{ required: true, whitespace: true, message: "Enter a description" }]}
			>
				<Input.TextArea
					autoSize={{ minRows: 2, maxRows: 5 }}
					placeholder="What must be true to pass this exit criteria"
				/>
			</Form.Item>
			{criterionType === "metric" ? (
				<>
					{/* metricSource is derived from the picked metric's group, never entered directly. */}
					<Form.Item name="metricSource" hidden>
						<Input />
					</Form.Item>
					<div className="rtp-cond-label">Condition</div>
					<div className="rtp-form-row">
						<Form.Item
							name="metricKey"
							className="rtp-form-row-wide"
							rules={[{ required: true, message: "Select a metric" }]}
						>
							<Select
								showSearch
								optionFilterProp="label"
								placeholder="Select a metric"
								aria-label="Metric"
								options={metricOptions}
								onChange={(value) => form.setFieldsValue({ metricSource: byKey[value]?.source })}
							/>
						</Form.Item>
						<Form.Item
							name="comparison"
							className="rtp-form-row-narrow"
							rules={[{ required: true, message: "Required" }]}
						>
							<Select options={COMPARISON_OPTIONS} placeholder=">=" aria-label="Comparison" />
						</Form.Item>
						<Form.Item name="threshold" rules={[{ required: true, message: "Enter a threshold" }]}>
							<InputNumber style={{ width: "100%" }} placeholder="Threshold" aria-label="Threshold" />
						</Form.Item>
					</div>
					{player ? (
						<MetricStatsHint
							player={player}
							metricKey={metricKey}
							applyTooltip="Click to use as the threshold"
							dataAttrPrefix="settings.rtp-criterion"
							onApply={(value) => form.setFieldsValue({ threshold: value })}
						/>
					) : null}
				</>
			) : null}
			<Form.Item
				name="allowedSigners"
				label="Staff allowed to sign off"
				rules={[{ required: true, type: "array", message: "Select at least one staff member" }]}
			>
				<Select
					mode="multiple"
					showSearch
					optionFilterProp="label"
					placeholder="Select staff who can sign off"
					options={[...coachList.map((c) => ({ value: c.uuid, label: coachName(c) })), ...extraSignerOptions]}
				/>
			</Form.Item>
		</>
	);
};
